export interface DeviceSettingApiResource {
  device_setting_field_id: string | null;
  field_name: string;
  field_unit: string | null;
  value: string;
  set_number: number;
}

export interface SessionTreatmentApiResource {
  id: string;
  treatment_id: string;
  treatment_name: string;
  body_area_id: string | null;
  body_area: string | null;
  outcome: string;
  response: string | null;
  device_settings: DeviceSettingApiResource[];
}

export interface SessionNotePhotoApiResource {
  id: string;
  url: string;
}

export interface SessionNoteApiResource {
  id: string;
  patient_id: string;
  appointment_id: string | null;
  pain_score: number | null;
  assessment: string | null;
  private_notes: string | null;
  status: string;
  body_area_ids: string[];
  body_areas: string[];
  treatments: SessionTreatmentApiResource[];
  photos: SessionNotePhotoApiResource[];
  created_at: string;
  updated_at: string;
}

export interface SessionNoteSummaryApiResource {
  id: string;
  appointment_id: string | null;
  status: string;
  pain_score: number | null;
  treatment_count: number;
  preview: string | null;
  author_name: string | null;
  visit_date: string | null;
  created_at: string;
}

export interface PerformedTreatmentApiResource {
  id: string;
  session_note_id: string;
  treatment_id: string;
  treatment_name: string;
  outcome: string;
  performed_on: string;
}

export interface DeviceSettingsHistoryEntryApiResource {
  session_note_id: string;
  treatment_id: string;
  treatment_name: string;
  recorded_at: string;
  device_settings: DeviceSettingApiResource[];
}

export interface DeviceSetting {
  deviceSettingFieldId: string | null;
  fieldName: string;
  fieldUnit: string | null;
  value: string;
  setNumber: number;
}

export interface SessionTreatment {
  id: string;
  treatmentId: string;
  treatmentName: string;
  bodyAreaId: string | null;
  bodyArea: string | null;
  outcome: string;
  response: string | null;
  deviceSettings: DeviceSetting[];
}

export interface SessionNotePhoto {
  id: string;
  url: string;
}

export interface SessionNote {
  id: string;
  patientId: string;
  appointmentId: string | null;
  painScore: number | null;
  assessment: string | null;
  privateNotes: string | null;
  status: string;
  bodyAreaIds: string[];
  bodyAreas: string[];
  treatments: SessionTreatment[];
  photos: SessionNotePhoto[];
  createdAt: string;
  updatedAt: string;
}

export interface SessionNoteSummary {
  id: string;
  appointmentId: string | null;
  status: string;
  painScore: number | null;
  treatmentCount: number;
  preview: string | null;
  authorName: string | null;
  visitDate: string | null;
  createdAt: string;
}

export interface PerformedTreatment {
  id: string;
  sessionNoteId: string;
  treatmentId: string;
  treatmentName: string;
  outcome: string;
  performedOn: string;
}

export interface DeviceSettingsHistoryEntry {
  sessionNoteId: string;
  treatmentId: string;
  treatmentName: string;
  recordedAt: string;
  deviceSettings: DeviceSetting[];
}

export interface DeviceSettingInput {
  deviceSettingFieldId: string;
  value: string;
  setNumber: number;
}

export interface SessionTreatmentInput {
  treatmentId: string;
  bodyAreaId: string | null;
  outcome: string;
  response: string | null;
  deviceSettings: DeviceSettingInput[];
}

export interface SessionNoteWritePayload {
  appointmentId: string | null;
  painScore: number | null;
  assessment: string | null;
  privateNotes: string | null;
  bodyAreaIds: string[];
  treatments: SessionTreatmentInput[];
  complete: boolean;
}

function mapDeviceSetting(resource: DeviceSettingApiResource): DeviceSetting {
  return {
    deviceSettingFieldId: resource.device_setting_field_id,
    fieldName: resource.field_name,
    fieldUnit: resource.field_unit,
    value: resource.value,
    setNumber: resource.set_number,
  };
}

function mapSessionTreatment(resource: SessionTreatmentApiResource): SessionTreatment {
  return {
    id: resource.id,
    treatmentId: resource.treatment_id,
    treatmentName: resource.treatment_name,
    bodyAreaId: resource.body_area_id,
    bodyArea: resource.body_area,
    outcome: resource.outcome,
    response: resource.response,
    deviceSettings: resource.device_settings.map(mapDeviceSetting),
  };
}

export function mapApiResourceToSessionNote(resource: SessionNoteApiResource): SessionNote {
  return {
    id: resource.id,
    patientId: resource.patient_id,
    appointmentId: resource.appointment_id,
    painScore: resource.pain_score,
    assessment: resource.assessment,
    privateNotes: resource.private_notes,
    status: resource.status,
    bodyAreaIds: resource.body_area_ids,
    bodyAreas: resource.body_areas,
    treatments: resource.treatments.map(mapSessionTreatment),
    photos: resource.photos.map(function toPhoto(photo) {
      return { id: photo.id, url: photo.url };
    }),
    createdAt: resource.created_at,
    updatedAt: resource.updated_at,
  };
}

export function mapApiResourceToSessionNoteSummary(
  resource: SessionNoteSummaryApiResource
): SessionNoteSummary {
  return {
    id: resource.id,
    appointmentId: resource.appointment_id,
    status: resource.status,
    painScore: resource.pain_score,
    treatmentCount: resource.treatment_count,
    preview: resource.preview,
    authorName: resource.author_name,
    visitDate: resource.visit_date,
    createdAt: resource.created_at,
  };
}

export function mapApiResourceToPerformedTreatment(
  resource: PerformedTreatmentApiResource
): PerformedTreatment {
  return {
    id: resource.id,
    sessionNoteId: resource.session_note_id,
    treatmentId: resource.treatment_id,
    treatmentName: resource.treatment_name,
    outcome: resource.outcome,
    performedOn: resource.performed_on,
  };
}

export function mapApiResourceToDeviceSettingsHistoryEntry(
  resource: DeviceSettingsHistoryEntryApiResource
): DeviceSettingsHistoryEntry {
  return {
    sessionNoteId: resource.session_note_id,
    treatmentId: resource.treatment_id,
    treatmentName: resource.treatment_name,
    recordedAt: resource.recorded_at,
    deviceSettings: resource.device_settings.map(mapDeviceSetting),
  };
}

export function mapApiResourceToDeviceSetting(resource: DeviceSettingApiResource): DeviceSetting {
  return mapDeviceSetting(resource);
}

export function mapSessionNoteWritePayload(
  payload: SessionNoteWritePayload
): Record<string, unknown> {
  return {
    appointment_id: payload.appointmentId,
    pain_score: payload.painScore,
    assessment: payload.assessment,
    private_notes: payload.privateNotes,
    body_area_ids: payload.bodyAreaIds,
    complete: payload.complete,
    treatments: payload.treatments.map(function mapTreatment(treatment) {
      return {
        treatment_id: treatment.treatmentId,
        body_area_id: treatment.bodyAreaId,
        outcome: treatment.outcome,
        response: treatment.response,
        device_settings: treatment.deviceSettings.map(function mapSetting(setting) {
          return {
            device_setting_field_id: setting.deviceSettingFieldId,
            value: setting.value,
            set_number: setting.setNumber,
          };
        }),
      };
    }),
  };
}
